/**
 * PushSubscribeButton — Push notification subscribe/unsubscribe toggle button.
 * Place it anywhere (e.g. user profile, notification center header).
 * - Fetches VAPID public key from backend (GET /api/push/vapid-key) on click
 * - Requests Notification permission (one-shot)
 * - Subscribes via PushManager → posts subscription to backend (POST /api/push/subscribe)
 */
import React, { useEffect, useState } from 'react';
import { Bell, BellOff, BellRing, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  registerServiceWorker, requestNotificationPermission,
  subscribeToPush, unsubscribeFromPush, getPushSubscription,
} from '../utils/pwa';

export default function PushSubscribeButton({ compact = false }) {
  const [status, setStatus] = useState('unknown'); // unknown | unsupported | denied | subscribed | unsubscribed
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setStatus('unsupported');
      return;
    }
    if (Notification.permission === 'denied') {
      setStatus('denied');
      return;
    }
    const sub = await getPushSubscription();
    setStatus(sub ? 'subscribed' : 'unsubscribed');
  };

  useEffect(() => { refresh(); }, []);

  const handleSubscribe = async () => {
    setBusy(true);
    try {
      const reg = await registerServiceWorker();
      if (!reg) { toast.error('Service worker registration failed'); return; }
      const granted = await requestNotificationPermission();
      if (!granted) { toast.error('Notification permission denied'); setStatus('denied'); return; }
      const sub = await subscribeToPush(reg);
      if (sub) {
        toast.success('🔔 Push notifications enabled');
        setStatus('subscribed');
      } else {
        toast.error('Subscribe failed — VAPID key may be missing on server');
      }
    } catch (e) {
      toast.error('Failed: ' + (e.message || e));
    } finally { setBusy(false); }
  };

  const handleUnsubscribe = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const ok = await unsubscribeFromPush(reg);
      if (ok) { toast.success('Unsubscribed'); setStatus('unsubscribed'); }
    } finally { setBusy(false); }
  };

  if (status === 'unsupported') {
    return compact ? null : (
      <div className="text-xs text-slate-500" data-testid="push-unsupported">Push notifications not supported on this browser.</div>
    );
  }
  if (status === 'denied') {
    return (
      <button disabled className="flex items-center gap-1.5 text-xs text-red-300 cursor-not-allowed px-2.5 py-1.5 bg-red-500/10 rounded-lg" title="Enable notifications in browser settings" data-testid="push-denied">
        <BellOff className="w-4 h-4" /> {compact ? '' : 'Notifications blocked'}
      </button>
    );
  }

  const subscribed = status === 'subscribed';
  const Icon = busy ? Loader2 : subscribed ? BellRing : Bell;
  return (
    <button
      onClick={subscribed ? handleUnsubscribe : handleSubscribe}
      disabled={busy}
      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition ${
        subscribed ? 'bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30' : 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30'
      } disabled:opacity-50`}
      title={subscribed ? 'Push notifications enabled — click to unsubscribe' : 'Click to enable browser push notifications'}
      data-testid={subscribed ? 'push-subscribed-btn' : 'push-subscribe-btn'}
    >
      <Icon className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />
      {!compact && (subscribed ? 'Push On' : 'Enable Push')}
    </button>
  );
}
